import { useMemo, useState } from 'react';
import { fmtAgo, fmtDate, useApi } from '../lib/hooks';
import { useMe } from '../lib/me';
import { api } from '../lib/api';
import { Link, navigate } from '../lib/router';
import { Icon } from '../components/Icon';
import { Avatar, Badge, Button, Empty, ErrorBox, Loading, Panel, RoleChip, StatusBadge, useAction, type RoleOption } from '../components/ui';
import { describeAction } from './Logs';

interface UserRow {
  id: string; kind: string; username: string; displayName: string; avatarUrl?: string; status: string; inGuild: boolean;
  discordRoleIds: string[]; localRoleIds: string[]; lastLoginAt?: string; lastSyncedAt?: string; joinedGuildAt?: string;
  google: boolean; password: boolean; discordId?: string;
}

export function useRoleOptions() {
  const { data } = useApi<{ roles: RoleOption[]; deletedRoles: { ref: string; name: string }[] }>('/api/admin/role-options');
  return useMemo(() => {
    const m = new Map<string, RoleOption>();
    for (const r of data?.roles ?? []) m.set(r.ref, r);
    for (const r of data?.deletedRoles ?? []) m.set(r.ref, { ref: r.ref, name: `${r.name} (deleted)`, color: '#e5484d', kind: 'discord' });
    return { options: data?.roles ?? [], byRef: m };
  }, [data]);
}

export function UsersPage() {
  const { data, error, loading, reload } = useApi<UserRow[]>('/api/admin/users');
  const { byRef } = useRoleOptions();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [role, setRole] = useState('');

  const rows = useMemo(() => (data ?? []).filter((u) => {
    if (status !== 'all' && u.status !== status) return false;
    if (role) {
      const [k, id] = role.split(':');
      if (!(k === 'discord' ? u.discordRoleIds : u.localRoleIds).includes(id)) return false;
    }
    const s = q.toLowerCase();
    return !s || u.displayName.toLowerCase().includes(s) || u.username.toLowerCase().includes(s) || (u.discordId ?? '').includes(s);
  }), [data, q, status, role]);

  if (loading && !data) return <Loading />;
  return (
    <div className="stack">
      <ErrorBox error={error} retry={reload} />
      <div className="toolbar">
        <div className="search"><Icon name="search" size={16} /><input placeholder="Search name, username or Discord ID" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="suspended">Suspended</option>
        </select>
        <select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Role">
          <option value="">Any role</option>
          {[...byRef.values()].filter((r) => !r.name.endsWith('(deleted)')).map((r) => <option key={r.ref} value={r.ref}>{r.name}{r.kind === 'local' ? ' (local)' : ''}</option>)}
        </select>
        <span className="muted small">{rows.length} of {data?.length ?? 0}</span>
      </div>
      <Panel pad={false}>
        {rows.length === 0 ? <Empty title="No users match" /> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Member</th><th>Status</th><th>Roles</th><th>Sign-in</th><th>Last login</th></tr></thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} className="clickable" onClick={() => navigate(`/admin/users/${u.id}`)}>
                    <td>
                      <div className="who">
                        <Avatar src={u.avatarUrl} name={u.displayName} size={30} />
                        <div><Link to={`/admin/users/${u.id}`} onClick={(e) => e.stopPropagation()}>{u.displayName}</Link><span className="muted small">@{u.username}</span></div>
                      </div>
                    </td>
                    <td><StatusBadge status={u.status} />{!u.inGuild && u.kind === 'discord' && <Badge tone="red">left Discord</Badge>}</td>
                    <td className="chips-cell">
                      {[...u.localRoleIds.map((r) => `local:${r}`), ...u.discordRoleIds.map((r) => `discord:${r}`)].slice(0, 5).map((ref) => {
                        const o = byRef.get(ref);
                        return <RoleChip key={ref} name={o?.name ?? '…'} color={o?.color} />;
                      })}
                      {u.discordRoleIds.length + u.localRoleIds.length > 5 && <span className="muted small">+{u.discordRoleIds.length + u.localRoleIds.length - 5}</span>}
                    </td>
                    <td className="signin-icons">
                      {u.kind === 'discord' && <span title="Discord"><Icon name="discord" size={16} /></span>}
                      {u.google && <span title="Google"><Icon name="google" size={16} /></span>}
                      {u.password && <span title="Password"><Icon name="key" size={16} /></span>}
                    </td>
                    <td className="muted" title={fmtDate(u.lastLoginAt)}>{fmtAgo(u.lastLoginAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

export function UserDetailPage({ id }: { id: string }) {
  const { data, error, loading, reload } = useApi<any>(`/api/admin/users/${id}`);
  const { data: roles } = useApi<any>('/api/admin/roles');
  const { can, me } = useMe();
  const { busy, run } = useAction();
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox error={error} retry={reload} />;
  if (!data) return null;
  const u = data.user;
  const manage = can('admin.users.manage');
  const self = me?.user.id === u.id;
  const setLocal = (ids: string[]) => run('roles', async () => { await api.patch(`/api/admin/users/${u.id}`, { localRoleIds: ids }); await reload(); }, 'Local roles updated');

  return (
    <div className="stack">
      <Link to="/admin/users" className="back"><Icon name="chevron" size={14} className="flip" /> All users</Link>
      <Panel>
        <div className="user-head">
          <Avatar src={u.avatarUrl} name={u.displayName} size={64} />
          <div className="grow">
            <h2>{u.displayName}</h2>
            <div className="muted">@{u.username} · {u.kind === 'discord' ? `Discord ${u.discordId}` : 'Local account'}</div>
            <div className="chips"><StatusBadge status={u.status} />{!u.inGuild && u.kind === 'discord' && <Badge tone="red">not in Discord</Badge>}{data.access.isAdmin && <Badge tone="gold">Platform Admin</Badge>}</div>
          </div>
          {manage && !self && (
            <div className="row-actions">
              {u.kind === 'discord' && <Button icon="refresh" busy={busy === 'sync'} onClick={() => run('sync', async () => { await api.post(`/api/admin/users/${u.id}/sync`); await reload(); }, 'User synced from Discord')}>Sync</Button>}
              <Button icon="logout" busy={busy === 'rev'} onClick={() => run('rev', () => api.post(`/api/admin/users/${u.id}/revoke-sessions`), 'Sessions revoked')}>Sign out</Button>
              {u.id !== 'l_breakglass' && (u.status === 'suspended'
                ? <Button variant="primary" busy={busy === 'st'} onClick={() => run('st', async () => { await api.patch(`/api/admin/users/${u.id}`, { status: 'active' }); await reload(); }, 'User reactivated')}>Reactivate</Button>
                : <Button variant="danger" busy={busy === 'st'} onClick={() => run('st', async () => { await api.patch(`/api/admin/users/${u.id}`, { status: 'suspended' }); await reload(); }, 'User suspended')}>Suspend</Button>)}
            </div>
          )}
        </div>
        <dl className="facts">
          <div><dt>Joined Discord</dt><dd>{fmtDate(u.joinedGuildAt, false)}</dd></div>
          <div><dt>First login</dt><dd>{fmtDate(u.firstLoginAt)}</dd></div>
          <div><dt>Last login</dt><dd>{fmtDate(u.lastLoginAt)}</dd></div>
          <div><dt>Last synced</dt><dd>{fmtDate(u.lastSyncedAt)}</dd></div>
          <div><dt>Sign-in methods</dt><dd>{[u.kind === 'discord' && 'Discord', u.google && `Google (${u.google.email})`, u.passwordLogin && `Password (${u.passwordLogin})`].filter(Boolean).join(', ') || '—'}</dd></div>
        </dl>
      </Panel>

      <div className="grid-2">
        <Panel title="Roles" icon="roles">
          <h4 className="sub">Discord roles <span className="muted small">(read-only, from Discord)</span></h4>
          <div className="chips">{data.roles.filter((r: any) => r.ref.startsWith('discord:')).map((r: any) => <RoleChip key={r.ref} name={r.name} color={r.color} />)}</div>
          <h4 className="sub">Local roles</h4>
          {roles && (
            <div className="local-role-list">
              {roles.local.map((r: any) => {
                const has = u.localRoleIds.includes(r.id);
                const locked = !manage || u.id === 'l_breakglass' || (r.id === 'platform-admin' && (!me?.access.isAdmin || self));
                return (
                  <label key={r.id} className={`check-row ${locked ? 'disabled' : ''}`}>
                    <input type="checkbox" checked={has} disabled={locked || busy === 'roles'}
                      onChange={() => setLocal(has ? u.localRoleIds.filter((x: string) => x !== r.id) : [...u.localRoleIds, r.id])} />
                    <i style={{ background: r.color }} /> <strong>{r.name}</strong>{r.requiresElevation && <Badge tone="amber">elevate</Badge>} <span className="muted small">{r.description}</span>
                  </label>
                );
              })}
            </div>
          )}
        </Panel>
        <Panel title="What they can access" icon="eye">
          {data.access.nav.length === 0 ? <p className="muted">No access.</p> : (
            <div className="clearance-summary">
              {data.access.nav.map((s: any) => (
                <div key={s.id}><strong><Icon name={s.icon} size={15} /> {s.name}</strong><ul>{s.tools.map((t: any) => <li key={t.id}>{t.name}</li>)}</ul></div>
              ))}
            </div>
          )}
          <h4 className="sub">Feature permissions</h4>
          <div className="chips">{Object.entries(data.access.features).filter(([, v]) => v).map(([k]) => <Badge key={k} tone="blue">{k}</Badge>)}</div>
        </Panel>
      </div>

      <Panel title="Recent activity by this user" icon="log">
        {data.recent.length === 0 ? <p className="muted">Nothing logged yet.</p> : (
          <ul className="activity">{data.recent.map((e: any) => <li key={e.id}><span className={`dot dot-${e.outcome}`} /><div><strong>{describeAction(e)}</strong><span className="muted small">{fmtDate(e.at)}</span></div></li>)}</ul>
        )}
      </Panel>
    </div>
  );
}
